// START IMPORTS
// Context imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Theme and UI imports
import { Card, Grid } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  useReport,
  ReportLoading,
  FullObituary,
  ObituaryHeader,
  ObituaryGeneralInformation,
  ReportNullState,
  ObituaryReport,
  DebugMenu,
  PersonSearchCta,
} from '@ltvco/refresh-lib/v1';
// Vendor imports
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface ObituaryReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

export function ObituaryReportPage({ permalink }: ObituaryReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const { trackEvent } = useContext(AppConfig);
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  trackEvent('view full report', 'view', 'obituary search');
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = new ObituaryReport(queryResult?.data);

  if (!report) return <ReportLoading menuItems={2} />;

  if (!report?.data?.obituaries && !report?.data?.obituaries?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const obituary = report.data.obituaries[0];

  const {
    name,
    dob,
    dod,
    funeralHome,
    funeralHomeAddress,
    funeralHomeUrl,
    message,
    photoUrl,
    nickname,
    personUrl,
    recentCity,
    recentRegion,
  } = obituary;

  const locationValue =
    recentCity && recentRegion ? `${recentCity}, ${recentRegion}` : null;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <>
      {/* START MAIN_CONTENT */}
      <Grid container direction={'row'} columns={12}>
        <Grid columns={12} marginTop={3}>
          <Card id="obituary-overview" sx={{ boxShadow: 'none' }}>
            <ObituaryHeader
              personName={name}
              nickname={nickname}
              personImage={photoUrl}
              age={''}
              dob={dob}
              dod={dod}
              funeralHome={funeralHome}
              funeralHomeAddress={funeralHomeAddress}
              funeralHomeUrl={funeralHomeUrl}
              photoUrl={photoUrl}
              personUrl={personUrl}
            />
            <Grid columns={8} margin="auto">
              <ObituaryGeneralInformation
                dob={dob}
                dod={dod}
                placeOfBirth={null}
                location={locationValue}
                imgInfinity={constants.images.imgInfinity}
              />
            </Grid>
          </Card>
          <Grid columns={8} margin="auto">
            <FullObituary text={message} personName={name} />
            <PersonSearchCta
              image={constants.images.imgPersonSearch}
              personName={name}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* END MAIN_CONTENT */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </>
  );
  // END RENDER
}
